import { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { adminApi } from "@/lib/api";
import { TID } from "@/lib/constants";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import DistillMark from "@/components/DistillMark";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import DraftCardEditor from "@/components/DraftCardEditor";
import ApiKeysTab from "@/pages/ApiKeysTab";
import { KeyRound, FilePlus2 } from "lucide-react";

export default function AdminDashboard() {
  const [cards, setCards] = useState([]);
  const [gazette, setGazette] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [stats, setStats] = useState({});
  const [audit, setAudit] = useState({ items: [], active_lockouts: [] });
  const [tab, setTab] = useState("drafts");
  const [loading, setLoading] = useState(true);
  const [changePwOpen, setChangePwOpen] = useState(false);
  const navigate = useNavigate();

  const drafts = useMemo(() => cards.filter((c) => c.status === "draft"), [cards]);
  const publishedCards = useMemo(() => cards.filter((c) => c.status !== "draft"), [cards]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, g, f, s] = await Promise.all([
        adminApi.listCards({ status: "all" }), adminApi.listGazette(), adminApi.feedback(), adminApi.stats(),
      ]);
      setCards(c.cards || []); setGazette(g.posts || []);
      setFeedback(f.items || []); setStats(s || {});
      try {
        const a = await adminApi.audit();
        setAudit(a || { items: [], active_lockouts: [] });
      } catch (err) {
        // Audit is optional (older admins may lack permission); log but don't fail the whole dashboard.
        console.warn("[admin] Audit fetch failed (non-fatal):", err?.response?.status || err?.message);
      }
    } catch (e) {
      if (e?.response?.status === 401) {
        // Cookie expired or missing — bounce to login.
        navigate("/studio/login", { replace: true });
      } else {
        console.error("[admin] Dashboard data load failed:", e);
        toast.error("Failed to load admin data");
      }
    } finally { setLoading(false); }
  }, [navigate]);

  useEffect(() => {
    // Session lives in an HttpOnly cookie now — the only way to know if we're
    // signed in is to probe the server. On 401 we redirect to login.
    let cancelled = false;
    (async () => {
      try {
        await adminApi.ping();
        if (!cancelled) load();
      } catch (e) {
        if (!cancelled && e?.response?.status === 401) {
          navigate("/studio/login", { replace: true });
        } else if (!cancelled) {
          console.error("[admin] Session probe failed:", e);
          navigate("/studio/login", { replace: true });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [load, navigate]);

  const del = async (kind, id) => {
    if (!window.confirm("Delete this permanently?")) return;
    try {
      if (kind === "card") await adminApi.deleteCard(id);
      else await adminApi.deleteGazette(id);
      toast.success("Deleted");
      load();
    } catch (err) {
      console.error("[admin] Delete failed:", err);
      toast.error("Delete failed");
    }
  };

  const logout = async () => {
    try { await adminApi.logout(); }
    catch (err) { console.warn("[admin] Logout request failed:", err); }
    navigate("/studio/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#111622]">
      <div className="mx-auto w-full max-w-[1100px] px-4 sm:px-6 py-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <DistillMark size={40} />
            <div>
              <h1 className="text-xl font-semibold tracking-[0.22em] text-[#E85A2A]">DISTILL</h1>
              <p className="text-xs text-[#94A3B8]" style={{ lineHeight: 1.6 }}>No BS, only GS. · Admin console</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Link to="/studio/cards/new" data-testid={TID.adminNewCard} className="rounded-xl bg-[#E85A2A] text-[#F8FAFC] px-4 py-2 text-sm font-semibold hover:bg-[#C94A1D] transition-colors">+ New Card</Link>
            <Link to="/studio/gazette/new" data-testid={TID.adminNewGazette} className="rounded-xl border border-[#26334D] bg-[#1A2232] px-4 py-2 text-sm font-semibold text-[#F8FAFC] hover:bg-[#232E45] transition-colors">+ New Gazette</Link>
            <button
              type="button"
              onClick={() => setChangePwOpen(true)}
              data-testid="admin-update-credentials-button"
              className="inline-flex items-center gap-2 rounded-xl border border-[#26334D] bg-[#1A2232] px-4 py-2 text-sm font-semibold text-[#F8FAFC] hover:bg-[#232E45] transition-colors"
              aria-label="Update admin email or password"
            >
              <KeyRound className="h-4 w-4" aria-hidden />
              Update credentials
            </button>
            <button onClick={logout} className="rounded-xl border border-[#26334D] bg-[#1A2232] px-4 py-2 text-sm font-semibold text-[#F8FAFC] hover:bg-[#232E45] transition-colors">Sign out</button>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-2 sm:grid-cols-5 gap-3">
          {[["cards", "Cards"], ["users", "Users"], ["bookmarks", "Bookmarks"], ["gazette", "Gazette"], ["feedback", "Feedback"]].map(([k, l]) => (
            <div key={k} className="rounded-2xl bg-[#1A2232] border border-[#26334D] p-4">
              <div className="text-xs text-[#94A3B8] uppercase tracking-wide">{l}</div>
              <div className="mt-1 text-xl font-semibold text-[#F8FAFC]">{stats[k] ?? 0}</div>
            </div>
          ))}
        </div>

        <div className="mt-6 flex gap-2 flex-wrap">
          {[
            ["drafts", "Drafts", drafts.length],
            ["cards", "Cards", null],
            ["gazette", "Gazette", null],
            ["feedback", "Feedback", null],
            ["apikeys", "API keys", null],
            ["security", "Security log", null],
          ].map(([k, l, count]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              data-testid={`admin-tab-${k}`}
              className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors ${tab === k ? "bg-[#E85A2A] text-[#F8FAFC] border-[#E85A2A]" : "bg-[#1A2232] text-[#F8FAFC] border-[#26334D] hover:bg-[#232E45]"}`}
            >
              {l}
              {count > 0 && (
                <span
                  className={`inline-flex items-center justify-center rounded-full min-w-[18px] h-[18px] px-1 text-[10px] font-bold ${tab === k ? "bg-[#F8FAFC] text-[#E85A2A]" : "bg-[#F2BD68] text-[#111622]"}`}
                  data-testid={`admin-tab-${k}-count`}
                >{count}</span>
              )}
            </button>
          ))}
        </div>

        <div className="mt-4">
          {loading ? (
            <div className="h-40 rounded-2xl bg-[#1A2232] border border-[#26334D] animate-pulse" />
          ) : tab === "drafts" ? (
            <div className="space-y-4" data-testid="admin-drafts-panel">
              {drafts.length === 0 ? (
                <div className="rounded-2xl bg-[#1A2232] border border-[#26334D] p-8 text-center" data-testid="admin-drafts-empty">
                  <div className="mx-auto h-12 w-12 rounded-full bg-[#111622] border border-[#26334D] flex items-center justify-center">
                    <FilePlus2 className="h-5 w-5 text-[#94A3B8]" aria-hidden />
                  </div>
                  <h3 className="mt-4 text-sm font-semibold text-[#F8FAFC]">No drafts pending</h3>
                  <p className="mt-1 text-xs text-[#94A3B8]" style={{ lineHeight: 1.6 }}>
                    New cards from n8n or the API ingest land here as drafts. You can also create one manually.
                  </p>
                  <Link
                    to="/studio/cards/new"
                    className="mt-4 inline-block rounded-xl bg-[#E85A2A] px-4 py-2 text-sm font-semibold text-[#F8FAFC] hover:bg-[#C94A1D] transition-colors"
                  >
                    Create card manually
                  </Link>
                </div>
              ) : (
                <>
                  <p className="text-xs text-[#94A3B8]" style={{ lineHeight: 1.6 }}>
                    <span className="text-[#F2BD68] font-semibold">{drafts.length}</span> draft{drafts.length === 1 ? "" : "s"} pending review. Public feed will not show these until you hit Publish.
                  </p>
                  {drafts.map((c) => (
                    <DraftCardEditor
                      key={c.card_id}
                      card={c}
                      onChanged={(updated) => {
                        setCards((prev) => prev.map((x) => (x.card_id === updated.card_id ? updated : x)));
                      }}
                      onDeleted={(id) => {
                        setCards((prev) => prev.filter((x) => x.card_id !== id));
                      }}
                      onPublished={(updated) => {
                        setCards((prev) => prev.map((x) => (x.card_id === updated.card_id ? updated : x)));
                      }}
                    />
                  ))}
                </>
              )}
            </div>
          ) : tab === "cards" ? (
            <div className="rounded-2xl bg-[#1A2232] border border-[#26334D] overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#111622] text-[#94A3B8]">
                  <tr>
                    <th className="text-left px-4 py-2 font-semibold">Headline</th>
                    <th className="text-left px-4 py-2 font-semibold">Status</th>
                    <th className="text-left px-4 py-2 font-semibold">Category</th>
                    <th className="text-left px-4 py-2 font-semibold">Published</th>
                    <th className="text-right px-4 py-2 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {publishedCards.map((c) => (
                    <tr key={c.card_id} data-testid={TID.adminRow} className="border-t border-[#26334D]">
                      <td className="px-4 py-3 text-[#F8FAFC] max-w-[420px]">{c.headline}</td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center rounded-full bg-[#2F6F4F]/15 border border-[#2F6F4F]/30 px-2 py-0.5 text-[10px] font-semibold text-[#7DE3B6] uppercase tracking-wide">
                          Live
                        </span>
                      </td>
                      <td className="px-4 py-3 text-[#94A3B8]">{c.category}{c.state_tag ? ` · ${c.state_tag}` : ""}</td>
                      <td className="px-4 py-3 text-[#94A3B8]">{formatDistanceToNow(new Date(c.published_at), { addSuffix: true })}</td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={async () => {
                            try {
                              const r = await adminApi.unpublishCard(c.card_id);
                              setCards((prev) => prev.map((x) => (x.card_id === r.card.card_id ? r.card : x)));
                              setTab("drafts");
                              toast.success("Moved to drafts.");
                            } catch (e) {
                              console.error("[admin] Unpublish failed:", e);
                              toast.error("Could not move to drafts.");
                            }
                          }}
                          className="text-[#F2BD68] font-semibold hover:underline mr-3"
                          data-testid="admin-unpublish"
                        >
                          To drafts
                        </button>
                        <Link to={`/studio/cards/${c.card_id}`} data-testid={TID.adminEdit} className="text-[#E85A2A] font-semibold hover:underline">Edit</Link>
                        <button onClick={() => del("card", c.card_id)} data-testid={TID.adminDelete} className="ml-3 text-[#B42318] font-semibold hover:underline">Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : tab === "gazette" ? (
            <div className="rounded-2xl bg-[#1A2232] border border-[#26334D] overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#111622] text-[#94A3B8]">
                  <tr>
                    <th className="text-left px-4 py-2 font-semibold">Title</th>
                    <th className="text-left px-4 py-2 font-semibold">Pinned</th>
                    <th className="text-left px-4 py-2 font-semibold">Published</th>
                    <th className="text-right px-4 py-2 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {gazette.map((g) => (
                    <tr key={g.post_id} className="border-t border-[#26334D]">
                      <td className="px-4 py-3 text-[#F8FAFC] max-w-[420px]">{g.title}</td>
                      <td className="px-4 py-3 text-[#94A3B8]">{g.is_pinned ? "Yes" : "No"}</td>
                      <td className="px-4 py-3 text-[#94A3B8]">{formatDistanceToNow(new Date(g.published_at), { addSuffix: true })}</td>
                      <td className="px-4 py-3 text-right">
                        <Link to={`/studio/gazette/${g.post_id}`} className="text-[#E85A2A] font-semibold hover:underline">Edit</Link>
                        <button onClick={() => del("gazette", g.post_id)} className="ml-3 text-[#B42318] font-semibold hover:underline">Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : tab === "feedback" ? (
            <div className="rounded-2xl bg-[#1A2232] border border-[#26334D]">
              <ul className="divide-y divide-[#26334D]">
                {feedback.length === 0 && <li className="p-6 text-center text-sm text-[#94A3B8]">No feedback yet.</li>}
                {feedback.map((f) => (
                  <li key={f.feedback_id} className="p-4">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${f.bucket === "keep" ? "bg-[#2F6F4F]/10 text-[#2F6F4F]" : f.bucket === "trash" ? "bg-[#B42318]/10 text-[#B42318]" : "bg-[#E85A2A]/10 text-[#E85A2A]"}`}>{f.bucket}</span>
                      <span className="text-xs text-[#94A3B8]">{f.user_email}</span>
                      <span className="ml-auto text-xs text-[#94A3B8]">{formatDistanceToNow(new Date(f.created_at), { addSuffix: true })}</span>
                    </div>
                    {f.text && <p className="mt-2 text-sm text-[#F8FAFC]" style={{ lineHeight: 1.6 }}>{f.text}</p>}
                  </li>
                ))}
              </ul>
            </div>
          ) : tab === "apikeys" ? (
            <ApiKeysTab />
          ) : (
            /* security tab */
            <div className="space-y-3">
              {audit.active_lockouts?.length > 0 && (
                <div className="rounded-2xl border border-[#B42318]/30 bg-[#B42318]/5 p-4">
                  <div className="text-sm font-semibold text-[#B42318]">Active lockouts</div>
                  <ul className="mt-2 space-y-1 text-xs text-[#F8FAFC]">
                    {audit.active_lockouts.map((l) => (
                      <li key={l.ip}><span className="font-mono">{l.ip}</span> — locked until {new Date(l.locked_until).toLocaleString()}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="rounded-2xl bg-[#1A2232] border border-[#26334D] overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-[#111622] text-[#94A3B8]">
                    <tr>
                      <th className="text-left px-4 py-2 font-semibold">Time</th>
                      <th className="text-left px-4 py-2 font-semibold">Result</th>
                      <th className="text-left px-4 py-2 font-semibold">IP</th>
                      <th className="text-left px-4 py-2 font-semibold">Email tried</th>
                      <th className="text-left px-4 py-2 font-semibold">User Agent</th>
                    </tr>
                  </thead>
                  <tbody>
                    {audit.items?.length === 0 && (
                      <tr><td colSpan={5} className="p-6 text-center text-sm text-[#94A3B8]">No login attempts logged yet.</td></tr>
                    )}
                    {audit.items?.map((a) => (
                      <tr key={a.audit_id} className="border-t border-[#26334D]">
                        <td className="px-4 py-2 text-[#94A3B8] whitespace-nowrap">{formatDistanceToNow(new Date(a.created_at), { addSuffix: true })}</td>
                        <td className="px-4 py-2">
                          <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${a.success ? "bg-[#2F6F4F]/10 text-[#2F6F4F]" : "bg-[#B42318]/10 text-[#B42318]"}`}>{a.success ? "success" : "failed"}</span>
                        </td>
                        <td className="px-4 py-2 font-mono text-xs">{a.ip}</td>
                        <td className="px-4 py-2 text-[#94A3B8]">{a.email}</td>
                        <td className="px-4 py-2 text-[#94A3B8] text-xs max-w-[280px] truncate">{a.user_agent || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      <ChangePasswordDialog open={changePwOpen} onClose={() => setChangePwOpen(false)} />
    </div>
  );
}
